import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';

const Applications = () => {
  const { language } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState(null);

  const content = {
    ru: {
      title: 'Сферы применения',
      items: [
        { text: 'Учебные заведения', desc: 'Школы, колледжи, университеты', image: '/images/gkl_school_main.webp' },
        { text: 'Административные здания', desc: 'Акиматы, госучреждения, офисы', image: '/images/gkl_admin_building.webp' },
        { text: 'Медицинские учреждения', desc: 'Больницы, поликлиники', image: '/images/comfort_business.webp' },
        { text: 'Отели и рестораны', desc: 'HoReCa сегмент', image: '/images/comfort_hotel.webp' },
        { text: 'Лобби и подъезды жилых комплексов', desc: 'Внеквартирные пространства', image: '/images/comfort_lobby.webp' },
        { text: 'Перегородки и коридоры', desc: 'Зонирование пространств', image: '/images/gkl_partitions.webp' }
      ]
    },
    kz: {
      title: 'Қолдану салалары',
      items: [
        { text: 'Оқу орындары', desc: 'Мектептер, колледждер, университеттер', image: '/images/gkl_school_main.webp' },
        { text: 'Әкімшілік ғимараттар', desc: 'Әкімдіктер, мемлекеттік мекемелер', image: '/images/gkl_admin_building.webp' },
        { text: 'Медициналық мекемелер', desc: 'Ауруханалар, емханалар', image: '/images/comfort_business.webp' },
        { text: 'Қонақүйлер мен мейрамханалар', desc: 'HoReCa сегменті', image: '/images/comfort_hotel.webp' },
        { text: 'Тұрғын үй кешендерінің лобби мен кіреберістері', desc: 'Пәтерден тыс кеңістіктер', image: '/images/comfort_lobby.webp' },
        { text: 'Аралық қабырғалар, дәліздер', desc: 'Кеңістікті аймақтау', image: '/images/gkl_partitions.webp' }
      ]
    },
    en: {
      title: 'Application Spheres',
      items: [
        { text: 'Educational institutions', desc: 'Schools, colleges, universities', image: '/images/gkl_school_main.webp' },
        { text: 'Administrative buildings', desc: 'Government offices', image: '/images/gkl_admin_building.webp' },
        { text: 'Medical facilities', desc: 'Hospitals, clinics', image: '/images/comfort_business.webp' },
        { text: 'Hotels and restaurants', desc: 'HoReCa segment', image: '/images/comfort_hotel.webp' },
        { text: 'Lobbies and residential entrances', desc: 'Common area spaces', image: '/images/comfort_lobby.webp' },
        { text: 'Partitions and corridors', desc: 'Space zoning', image: '/images/gkl_partitions.webp' }
      ]
    },
    ar: {
      title: 'مجالات التطبيق',
      items: [
        { text: 'المؤسسات التعليمية', desc: 'المدارس والكليات والجامعات', image: '/images/gkl_school_main.webp' },
        { text: 'المباني الإدارية', desc: 'المكاتب الحكومية', image: '/images/gkl_admin_building.webp' },
        { text: 'المرافق الطبية', desc: 'المستشفيات والعيادات', image: '/images/comfort_business.webp' },
        { text: 'الفنادق والمطاعم', desc: 'قطاع HoReCa', image: '/images/comfort_hotel.webp' },
        { text: 'البهوات ومداخل المباني السكنية', desc: 'فضاءات المناطق المشتركة', image: '/images/comfort_lobby.webp' },
        { text: 'الفواصل والممرات', desc: 'تقسيم الفضاءات', image: '/images/gkl_partitions.webp' }
      ]
    }
  };

  const t = content[language] || content.en || content.ru;

  return (
    <section className="pt-6 py-2 pb-6 bg-[#061d36]">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-8 md:mb-12">{t.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
          {t.items.map((app, idx) => (
            <div 
              key={idx} 
              className="group rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 cursor-pointer shadow-sm hover:shadow-lg bg-[#F5F5F0]"
              onClick={() => setLightboxImage({ src: app.image, text: app.text })}
              data-testid={`application-card-${idx}`}
            >
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={app.image} 
                  alt={app.text} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-3 text-center">
                <h3 className="font-semibold text-[#061d36] text-sm mb-1">{app.text}</h3>
                <p className="text-xs text-[#061d36]/60">{app.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img 
              src={lightboxImage.src} 
              alt={lightboxImage.text} 
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <p className="text-white text-lg font-medium mt-4">{lightboxImage.text}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Applications;
